import React, { useEffect, useState } from 'react';
import adminApi from '../api/client';

interface ContactSettings {
  contact_enabled: boolean;
  personal_active_limit: number;
  revision: number;
}

export default function MarketplaceContactSettings() {
  const [settings, setSettings] = useState<ContactSettings | null>(null);
  const [limit, setLimit] = useState('20');
  const [reason, setReason] = useState('');
  const [pause, setPause] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminApi.get<ContactSettings>('/admin/marketplace/contact-settings');
      setSettings(res.data);
      setLimit(String(res.data.personal_active_limit));
      setPause(false);
    } catch (err: any) {
      setError(err.response?.status === 404 ? 'Contact controls are unavailable until the prepared schema is installed.' : 'Failed to load contact settings.');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!settings || saving || !reason.trim() || !/^\d+$/.test(limit) || Number(limit) < 1 || Number(limit) > 100) return;
    if (!window.confirm(pause ? 'Pause contact marketplace and save this limit? Existing conversations and owner history remain available.' : 'Save the personal active-listing limit? Existing listings remain; future publishing uses this limit.')) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await adminApi.put<ContactSettings>('/admin/marketplace/contact-settings', {
        expected_revision: settings.revision, personal_active_limit: Number(limit), pause_contact: pause, reason: reason.trim(),
      });
      setSettings(res.data);
      setLimit(String(res.data.personal_active_limit));
      setPause(false);
      setReason('');
      setMessage('Contact settings saved.');
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to save contact settings.');
    } finally { setSaving(false); }
  };

  return <section className="bg-white rounded-2xl p-6 shadow-sm space-y-4" aria-label="Contact marketplace controls">
    <h2 className="text-lg font-bold">Personal used-book marketplace</h2>
    {loading ? <p role="status">Loading contact settings…</p> : <>
      {settings && <form onSubmit={save} className="space-y-3">
        <p>Contact selling: <strong>{settings.contact_enabled ? 'Enabled' : 'Paused'}</strong>. Public activation is managed through the coordinated launch process.</p>
        <p className="text-sm text-gray-600">These controls apply to personal contact listings. Commercial orders and payments use their existing controls.</p>
        <label className="block">Personal active-listing limit
          <input aria-label="Personal active-listing limit" type="number" min="1" max="100" step="1" required value={limit} disabled={saving} onChange={e => setLimit(e.target.value)} className="border rounded p-2 ml-3" />
        </label>
        {settings.contact_enabled && <label className="block"><input type="checkbox" checked={pause} disabled={saving} onChange={e => setPause(e.target.checked)} /> Pause contact marketplace</label>}
        <label className="block">Reason for settings change
          <textarea aria-label="Reason for settings change" required maxLength={500} value={reason} disabled={saving} onChange={e => setReason(e.target.value)} className="border rounded p-2 block w-full" />
        </label>
        <button type="submit" disabled={saving || !reason.trim() || !/^\d+$/.test(limit) || Number(limit) < 1 || Number(limit) > 100} className="bg-[#085041] text-white rounded px-4 py-2 disabled:opacity-50">{saving ? 'Saving…' : 'Save contact settings'}</button>
      </form>}
      <button type="button" onClick={load} disabled={saving} className="underline">Reload contact settings</button>
    </>}
    {error && <p role="alert" className="text-red-700">{error}</p>}
    {message && <p role="status">{message}</p>}
  </section>;
}
